// @refresh reset
import * as Sentry from '@sentry/nextjs'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import { Container, LoadingDots } from 'components/ui'
import { ProductCarousel } from 'modules/products/components'
import Layout, { Breadcrumb, BreadcrumbProps } from 'components/layout'
import dynamic from 'next/dynamic'
import type { Product } from 'framework/products'
import { useEffect, useState, useCallback } from 'react'
import { FourInlineCards } from 'components/cards'
import parser from 'fazendinha/product'
import sellerParser from 'fazendinha/seller'
import { ProductService } from 'modules/products/services'
import { ProductCard, ProductView } from 'modules/products/containers'
import { Seller } from 'framework/sellers'
import { SellerService } from 'modules/sellers/services'
import { ROUTE_PRODUCT_INDEX } from 'routes/products'
import { useCart } from 'hooks/use-cart'
import { useMercadinho } from 'hooks/use-mercadinho'
import s from './product.module.css'
import Cookies from 'js-cookie'
import { useAfiliado } from 'hooks/use-afiliado'
import AffiliateService from 'modules/affiliates/services/affiliate-service'

interface ProductPageProps {
  product: Product & { categories: any; categorias_slug: string[] }
  slug: string
  categories: { slug: string; value: string }
}

const dynamicProps = {
  loading: () => <LoadingDots />,
}

interface Params {
  params: {
    slug: string
  }
}

const SellerProfile = dynamic(() => import('modules/sellers/components/seller-profile'), dynamicProps)

export const getServerSideProps = async ({ params }: Params) => {
  const productService = new ProductService()
  try {
    const { slug } = params
    const {
      data: { data },
    } = await productService.getById(slug)

    return {
      props: {
        product: parser(data),
        categories: {
          slug: data.categorias_slug[0],
          value: data.categorias_descricao[0],
        },
        slug,
      },
    }
  } catch (e: any) {
    if (e?.response?.status === 404) {
      Sentry.captureMessage(`Produto não encontrado: ${params.slug}`, {
        level: 'info',
        tags: { slug: params.slug },
      })
      return {
        redirect: {
          permanent: false,
          destination: '/produto/nao-encontrado',
        },
      }
    }
    return {
      redirect: {
        permanent: false,
        destination: '/',
      },
    }
  }
}

interface SellerProducts extends Array<Product> {}

function StarFilled(props: any) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="#FFD700" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  )
}
function StarOutline(props: any) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  )
}

function ProductPage({ product, categories }: ProductPageProps) {
  const router = useRouter()
  const { zipCode } = useCart()
  const { isAfiliado } = useAfiliado()
  const [isFavorite, setIsFavorite] = useState(product.isAfiliadoFavorite)
  const [loadingFavorite, setLoadingFavorite] = useState(false)

  // Salvar afiliadoId do querystring nos cookies
  useEffect(() => {
    if (router && router.query && router.query.afiliadoid) {
      Cookies.set('afiliadoId', router.query.afiliadoid, { expires: 5 })
    }
  }, [router.query.afiliadoid])

  const [seller, setSeller] = useState<Seller>(null as unknown as Seller)
  const [sellerProducts, setSellerProducts] = useState<SellerProducts>([] as SellerProducts)
  const { setIsMercadinho } = useMercadinho()

  const getProducts = useCallback(() => {
    const productService = new ProductService()
    return productService.query({
      categories: '',
      page: 1,
      search: '',
      seller: product.seller.id,
      zipcode: zipCode,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.seller.id])

  const getSeller = useCallback(() => {
    const sellerService = new SellerService()
    return sellerService.getBySlug(product.seller.slug)
  }, [product.seller])

  useEffect(() => {
    setIsMercadinho(false)
    getProducts()
      .then(({ data: { data } }: { data: { data: any[] } }) => {
        setSellerProducts(data.map((p) => parser(p)))
      })
      .catch(() => {})
    getSeller()
      .then(({ data: { data } }: { data: { data: any } }) => {
        setSeller(sellerParser(data as unknown as any))
      })
      .catch(() => {})
  }, [getProducts, getSeller])

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (loadingFavorite) return
    setLoadingFavorite(true)
    try {
      const affiliateService = new AffiliateService()
      if (isFavorite) {
        await affiliateService.setUrl(`/afiliados/delete-produto-favorito?produto_id=${product.id}`).delete()
        setIsFavorite(false)
      } else {
        await affiliateService.setUrl(`/afiliados/add-produto-favorito`).post({ produto_id: product.id })
        setIsFavorite(true)
      }
    } catch (err) {
      // Pode exibir um toast de erro se desejar
    } finally {
      setLoadingFavorite(false)
    }
  }

  const crumbs: BreadcrumbProps['crumbs'] = [
    { url: ROUTE_PRODUCT_INDEX, label: 'Produtos' },
    { url: ROUTE_PRODUCT_INDEX + categories.slug, label: categories.value },
    { label: `#${product.id}` },
  ]

  if (router.isFallback) {
    return <LoadingDots />
  }

  return (
    <Layout>
      <NextSeo
        title={product.name}
        description={product.description}
        openGraph={{
          type: 'website',
          title: product.name,
          description: product.description,
          images: [
            {
              url: product.image,
              width: 800,
              height: 600,
              alt: product.name,
            },
          ],
        }}
      />
      <Breadcrumb crumbs={crumbs} />
      <div className={s.view}>
        <ProductView 
          key="product-view" 
          product={{ ...product, seller }} 
          isAfiliado={isAfiliado}
          isFavorite={isFavorite}
          loadingFavorite={loadingFavorite}
          onToggleFavorite={handleToggleFavorite}
          StarFilled={StarFilled}
          StarOutline={StarOutline}
        />
      </div>
      <Container className={s.root}>
        <FourInlineCards
          cards={[
            {
              icon: <img src="/assets/icons/inline-cards/farm-products.svg" alt="" width="60px" />,
              title: 'Produtos Fresquinhos',
              description: '',
            },
            {
              icon: <img src="/assets/icons/inline-cards/farm-tractor.svg" alt="" width="60px" />,
              title: 'Frete único de baixo custo',
              description: '',
            },
            {
              icon: <img src="/assets/icons/inline-cards/milk-products.svg" alt="" width="60px" />,
              title: 'Produtores selecionados',
              description: '',
            },
            {
              icon: <img src="/assets/icons/inline-cards/produtor.svg" alt="" width="60px" />,
              title: 'Hortifruti sem agrotóxicos',
              description: '',
            },
          ]}
        />
        {seller && (
          <div className={s.sellerSection}>
            <SellerProfile
              key="product-section-seller-profile"
              seller={seller}
              className={s.seller}
              withHeader={false}
            />
            {sellerProducts.length > 0 && (
              <ProductCarousel
                key="product-section-carousel"
                title="Mais produtos deste produtor"
                rightContent={{ href: `/produtores/${product.seller.slug}`, title: 'Veja todos' }}
                className={s.productCarousel}
              >
                {sellerProducts.map((sellerProduct) => (
                  <ProductCard key={`seller-product-${sellerProduct.id}`} product={sellerProduct} />
                ))}
              </ProductCarousel>
            )}
          </div>
        )}
      </Container>
    </Layout>
  )
}

export default ProductPage
